/**
 * @Description: 数据可视化轨迹图接口，https://lbs.qq.com/webApi/visualizationApi/visualizationDoc/visualizationDocTrail#2
 * @date 2022/9/6
 * @params
*/
import { Ref, WatchStopHandle } from 'vue';
import omit from 'lodash/omit';
import isNumber from 'lodash/isNumber';
import { LAYER_CONFIG_OMIT_PROP } from '../constants';
const reInitProps = ['enableHighlightPoint'];
export default function useTrailLayer(mapInstance: LooseObject, layerInfo: Ref<MapLayer<TrailLayerConfig>>) {
  if (!layerInfo.value || !layerInfo.value.config || !TMap.visualization?.Trail) return;

  const options = omit(layerInfo.value.config, LAYER_CONFIG_OMIT_PROP);

  let unwatchList: WatchStopHandle[] | null = [];

  const trailOptions = {
    ...omit(options, 'zoomRange', 'gradientColor'),
    width: isNumber(options.width) ? options.width : undefined,
    showDuration: options.showDuration,
    playRate: options.playRate,
    playTimes: options.playTimes,
    minZoom: options.zoomRange?.[0],
    maxZoom: options.zoomRange?.[1],
    color: options.color,
    enableHighlightPoint: options.enableHighlightPoint,
  };

  let trailLayer = new TMap.visualization.Trail(trailOptions);
  trailLayer.addTo(mapInstance);

  unwatchList.push(watchEffect(() => {
    try {
      // seiya 此处setData 空数据有bug，兼容处理：空数据的时候隐藏，
      if (layerInfo.value.config.data.length === 0) {
        trailLayer?.hide();
      } else {
        trailLayer?.show();
        trailLayer?.setData(layerInfo.value.config.data);
      }
    } catch (error) {
      trailLayer?.setData([]);
    }
  }));
  Object.keys(options).forEach((key) => {
    unwatchList?.push(watch(() => layerInfo.value.config[key], (newVal) => {
      // 部分属性没有对应set方法,需要重新初始化地图
      if (reInitProps.includes(key)) {
        trailLayer = new TMap.visualization.Trail(trailOptions);
        return;
      }
      trailOptionsHandlers[key](newVal, trailLayer);
    }, { deep: true }));
  });

  trailLayer.unmount = (): void => {
    trailLayer?.destroy();
    unwatchList?.forEach(unwatch => unwatch());
    unwatchList = null;
  };

  return trailLayer;
}
// 对轨迹图层每个属性响应
const trailOptionsHandlers = {
  width(val: number, layerInstance: LooseObject): void {
    if (!isNumber(val)) {
      console.warn('请传入数字类型，当前传入的是：', val);
      return;
    }
    layerInstance.setPickStyle((item: any) =>
      // 轨迹图样式映射函数
      ({
        width: val,
        color: layerInstance.getPickStyle(item).color || '#387CEA',
      }));
  },
  color(val: LooseObject | string, layerInstance: LooseObject): void {
    if (!val) return;
    layerInstance.setPickStyle((item: any) =>
    // 轨迹图样式映射函数
      ({
        width: layerInstance.getPickStyle(item).width || 4,
        color: typeof val === 'string'
          ? val
          : new TMap.GradientColor({
            stops: val,
          }),
      }));
  },
  showDuration(val: number, layerInstance: LooseObject): void {
    layerInstance.setShowDuration(val);
  },
  playRate(val: number, layerInstance: LooseObject): void {
    layerInstance.setPlayRate(val);
  },
  playTimes(val: number, layerInstance: LooseObject): void {
    layerInstance.setPlayTimes(val);
  },
  zoomRange(val: TrailLayerConfig['zoomRange'], layerInstance: LooseObject): void {
    layerInstance.setMinZoom(val?.[0]);
    layerInstance.setMaxZoom(val?.[1]);
  },
};
